import type JSZip from 'jszip';

/**
 * 打开一个主题压缩包。
 *
 * 为什么要收 zip 而不是"把几份文件一起选中"：主题包是 4 份 JSON + 2 张图，
 * 一次多选很容易漏掉其中一份，而漏掉的症状不是报错 —— `composeThemeManifest`
 * 会安静地少合并一块，于是发布出去的主题缺变体或缺页面模板，谁也不会立刻发现。
 * 一个压缩包天然不会漏，也正好是工坊导出的形态。
 *
 * 这里只做**拆包与分拣**，不做业务判断（合并、校验、上传都在调用方）：
 * 一个纯函数才测得出"包里带子目录""有 __MACOSX""两张 preview.png"这些情况。
 */

export interface ZipJsonFile {
  name: string;
  json: unknown;
}

export interface ZipImageFile {
  name: string;
  file: File;
}

export interface OpenedThemeZip {
  json: ZipJsonFile[];
  /** 市场卡片图：包里的 `preview.png` */
  card?: ZipImageFile;
  /** 详情大图：包里的 `preview-hero.png` */
  hero?: ZipImageFile;
  /** 其余图片（logo、截图素材…） */
  otherImages: string[];
  /** 被跳过的路径。回给界面，让人知道"我明明放了却没读到"是为什么 */
  ignored: string[];
}

export class ThemeZipError extends Error {}

/** 包里的图只有这三种；svg 不收（市场卡片是位图，且 svg 能带脚本） */
const IMAGE_MIME: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

/** 解压上限。主题包正常 1~3MB，超过这个量级不是主题包 */
const MAX_ENTRIES = 200;
const MAX_TOTAL_BYTES = 24 * 1024 * 1024;
const MAX_JSON_BYTES = 2 * 1024 * 1024;

function basename(path: string): string {
  const parts = path.split('/');
  return parts[parts.length - 1] ?? '';
}

function extOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot < 0 ? '' : name.slice(dot + 1).toLowerCase();
}

/**
 * macOS 打的包会把资源分叉塞进 `__MACOSX/`，里面常有一个同名的 `._preview.png`。
 * 不跳过的话，卡片图会被一张 4KB 的垃圾覆盖。
 */
function shouldIgnore(path: string): boolean {
  const base = basename(path);
  if (path.startsWith('__MACOSX/') || path.includes('/__MACOSX/')) return true;
  if (base.startsWith('._')) return true;
  if (base === '.DS_Store' || base === 'Thumbs.db' || base === 'desktop.ini') return true;
  if (base.startsWith('.')) return true;
  return false;
}

export async function openThemeZip(file: File): Promise<OpenedThemeZip> {
  if (!/\.zip$/i.test(file.name)) {
    throw new ThemeZipError(`只收 .zip 压缩包，选到的是「${file.name}」`);
  }

  let zip: JSZip;
  try {
    /**
     * 按需加载：解压库有 100KB 上下，而它只在"选了包"那一刻用得上。
     * 静态 import 会让每个进控制台的人都先下载它一遍。
     */
    const { default: JSZipLoader } = await import('jszip');
    zip = await JSZipLoader.loadAsync(file);
  } catch (err) {
    // 动态加载失败（离线、CDN 挂）与"文件坏了"要分开说，否则会让人去重打压缩包
    if (err instanceof Error && /Loading chunk|dynamically imported|Failed to fetch/i.test(err.message)) {
      throw new ThemeZipError('解压组件没加载出来，刷新页面再试一次');
    }
    throw new ThemeZipError('这个文件解不开，可能不是 zip，或者压缩时被截断了');
  }

  // `Object.values` 里已经排除了目录（`dir: true`），但文件系统条目仍可能带目录位
  const entries = Object.values(zip.files).filter((e) => !e.dir && !shouldIgnore(e.name));
  if (entries.length === 0) {
    throw new ThemeZipError('压缩包是空的，或者里面只有 macOS 生成的隐藏文件');
  }
  if (entries.length > MAX_ENTRIES) {
    throw new ThemeZipError(`压缩包里有 ${entries.length} 个文件，不像主题包`);
  }

  const json: ZipJsonFile[] = [];
  const ignored: string[] = [];
  const images = new Map<string, ZipImageFile>();
  const otherImages: string[] = [];
  let total = 0;

  for (const entry of entries) {
    const base = basename(entry.name);
    const ext = extOf(base);

    if (ext === 'json') {
      const text = await entry.async('string');
      if (text.length > MAX_JSON_BYTES) {
        throw new ThemeZipError(`「${base}」有 ${Math.round(text.length / 1024)}KB，不像配置文件`);
      }
      total += text.length;
      if (total > MAX_TOTAL_BYTES) {
        throw new ThemeZipError('压缩包解压后太大，主题包正常只有几 MB');
      }
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch (err) {
        throw new ThemeZipError(`「${base}」不是合法的 JSON：${(err as Error).message}`);
      }
      json.push({ name: base, json: parsed });
      continue;
    }

    const mime = IMAGE_MIME[ext];
    if (!mime) {
      // 字体、说明文档、源代码…这里不认，但要说出来，否则"我放了 README"会变成静默丢弃
      ignored.push(entry.name);
      continue;
    }

    const blob = await entry.async('blob');
    total += blob.size;
    if (total > MAX_TOTAL_BYTES) {
      throw new ThemeZipError('压缩包解压后太大，主题包正常只有几 MB');
    }
    const name = base.toLowerCase();

    /**
     * 只认这两个名字，而且**不靠目录**：工坊导出时会把整个主题文件夹压进去，
     * 图在 `hubs/preview.png`，按路径匹配就会漏。
     */
    if (name === 'preview.png' || name === 'preview.jpg' || name === 'preview.jpeg' || name === 'preview.webp') {
      if (images.has('card')) throw new ThemeZipError('包里有不止一张 preview.png，不知道该用哪张');
      images.set('card', { name: base, file: new File([blob], base, { type: mime }) });
      continue;
    }
    if (
      name === 'preview-hero.png' ||
      name === 'preview-hero.jpg' ||
      name === 'preview-hero.jpeg' ||
      name === 'preview-hero.webp'
    ) {
      if (images.has('hero')) throw new ThemeZipError('包里有不止一张 preview-hero.png，不知道该用哪张');
      images.set('hero', { name: base, file: new File([blob], base, { type: mime }) });
      continue;
    }
    otherImages.push(base);
  }

  if (json.length === 0) {
    throw new ThemeZipError('包里没有 JSON 文件 —— 主题包至少要有 theme.json 与 tokens.json');
  }

  return {
    json,
    card: images.get('card'),
    hero: images.get('hero'),
    otherImages,
    ignored,
  };
}
